// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatMessage, ChatMessageSearchFilters, ChatMessageCode } from '../../types/chatMessages';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './ChatMessages.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const PagePrevIcon = () => (
  <img src="/static/svg/page-prev.svg" alt="Previous page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const ChatMessages: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [prevCursor, setPrevCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<ChatMessageSearchFilters>({
    channel_id: '',
    sender_id: '',
    username: '',
    content: '',
    room_name: '',
    group_id: '',
    code: undefined,
    persistent: undefined,
  });

  const sessionRole = authService.getSessionRole();

  // Initialize filters from URL params
  useEffect(() => {
    const channelId = searchParams.get('channel_id') || '';
    const senderId = searchParams.get('sender_id') || '';
    const username = searchParams.get('username') || '';
    const content = searchParams.get('content') || '';
    const roomName = searchParams.get('room_name') || '';
    const groupId = searchParams.get('group_id') || '';
    const code = searchParams.get('code');
    const persistent = searchParams.get('persistent');
    const cursor = searchParams.get('cursor') || '';

    setSearchFilters({
      channel_id: channelId,
      sender_id: senderId,
      username,
      content,
      room_name: roomName,
      group_id: groupId,
      code: code ? parseInt(code) : undefined,
      persistent: persistent ? persistent === 'true' : undefined,
    });
    setNextCursor(cursor);

    if (cursor || channelId || senderId || username || content || roomName || groupId || code || persistent) {
      loadMessages(cursor, {
        channel_id: channelId,
        sender_id: senderId,
        username,
        content,
        room_name: roomName,
        group_id: groupId,
        code: code ? parseInt(code) : undefined,
        persistent: persistent ? persistent === 'true' : undefined,
      });
    } else {
      loadMessages();
    }
  }, []);

  const loadMessages = useCallback(async (
    cursor?: string,
    filters?: ChatMessageSearchFilters,
    forward: boolean = true
  ) => {
    setLoading(true);
    setError('');

    try {
      // Determine chat type based on filters
      const currentFilters = filters || searchFilters;
      let chatType: number = 2; // Default to room messages
      let label: string | undefined;
      
      if (currentFilters.room_name) {
        chatType = 2; // Room messages
        // Use room_name as label, or default to '0' if empty (matching old UI behavior)
        label = currentFilters.room_name.trim() || '0';
      } else if (currentFilters.group_id) {
        chatType = 3; // Group messages
      } else if (currentFilters.channel_id && currentFilters.channel_id.includes('+')) {
        // Direct message channel IDs typically contain '+'
        chatType = 4; // Direct messages
      } else {
        // Default case - for general queries, use type 2 with default label
        chatType = 2;
        label = '0';
      }

      const filtersWithType = {
        ...currentFilters,
        type: chatType,
        label: label
      };

      const result = await consoleService.listChatMessages(
        '',
        filtersWithType,
        cursor,
        forward
      );

      setMessages(result.messages || []);
      setNextCursor(result.next_cursor || '');
      setPrevCursor(result.prev_cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load chat messages');
    } finally {
      setLoading(false);
    }
  }, [searchFilters]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    let forward = true;
    
    switch (direction) {
      case -1:
        cursor = prevCursor;
        forward = false;
        break;
      case 0:
        cursor = '';
        forward = true;
        break;
      case 1:
        cursor = nextCursor;
        forward = true;
        break;
    }

    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.channel_id) params.set('channel_id', searchFilters.channel_id);
    if (searchFilters.sender_id) params.set('sender_id', searchFilters.sender_id);
    if (searchFilters.username) params.set('username', searchFilters.username);
    if (searchFilters.content) params.set('content', searchFilters.content);
    if (searchFilters.room_name) params.set('room_name', searchFilters.room_name);
    if (searchFilters.group_id) params.set('group_id', searchFilters.group_id);
    if (searchFilters.code !== undefined) params.set('code', searchFilters.code.toString());
    if (searchFilters.persistent !== undefined) params.set('persistent', searchFilters.persistent.toString());
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadMessages(cursor, searchFilters, forward);
  }, [searchFilters, nextCursor, prevCursor, setSearchParams, loadMessages]);

  const handleFilterChange = (field: keyof ChatMessageSearchFilters, value: string | number | boolean | undefined) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleDeleteMessage = (message: ChatMessage, index: number) => {
    openConfirmDialog(
      'Delete Message',
      `Are you sure you want to delete this message from "${message.username || message.sender_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteChatMessage('', {
            message_id: message.message_id,
            channel_id: message.channel_id
          });
          setMessages(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete message');
        }
      }
    );
  };

  const handleViewMessage = (message: ChatMessage) => {
    navigate(`/chat-messages/${message.message_id}?channel_id=${message.channel_id}`);
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getMessageTypeText = (code: number) => {
    switch (code) {
      case ChatMessageCode.CHANNEL_JOIN: return 'Join';
      case ChatMessageCode.CHANNEL_LEAVE: return 'Leave';
      case ChatMessageCode.CHANNEL_MESSAGE: return 'Message';
      case ChatMessageCode.CHANNEL_UPDATE: return 'Update';
      case ChatMessageCode.CHANNEL_REMOVE: return 'Remove';
      default: return 'Unknown';
    }
  };

  const getMessageTypeBadgeClass = (code: number) => {
    switch (code) {
      case ChatMessageCode.CHANNEL_JOIN: return 'join';
      case ChatMessageCode.CHANNEL_LEAVE: return 'leave';
      case ChatMessageCode.CHANNEL_MESSAGE: return 'message';
      case ChatMessageCode.CHANNEL_UPDATE: return 'update';
      case ChatMessageCode.CHANNEL_REMOVE: return 'remove';
      default: return 'unknown';
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const getChannelType = (message: ChatMessage) => {
    if (message.room_name) return 'Room';
    if (message.group_id) return 'Group';
    if (message.user_id_one && message.user_id_two) return 'Direct';
    return 'Unknown';
  };

  return (
    <div className="chat-messages-container">
      <div className="chat-messages-header">
        <h2 className="chat-messages-title">Chat Messages</h2>
        <h6 className="chat-messages-subtitle">{messages.length} messages found.</h6>
      </div>

      <div className="chat-messages-controls">
        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                className="search-input"
                value={searchFilters.channel_id || ''}
                onChange={(e) => handleFilterChange('channel_id', e.target.value)}
                placeholder="Filter by channel ID"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.sender_id || ''}
                onChange={(e) => handleFilterChange('sender_id', e.target.value)}
                placeholder="Filter by sender ID"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.username || ''}
                onChange={(e) => handleFilterChange('username', e.target.value)}
                placeholder="Filter by username"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.content || ''}
                onChange={(e) => handleFilterChange('content', e.target.value)}
                placeholder="Filter by content"
              />
              <select
                className="code-select"
                value={searchFilters.code || ''}
                onChange={(e) => handleFilterChange('code', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">All Types</option>
                <option value={ChatMessageCode.CHANNEL_JOIN}>Join</option>
                <option value={ChatMessageCode.CHANNEL_LEAVE}>Leave</option>
                <option value={ChatMessageCode.CHANNEL_MESSAGE}>Message</option>
                <option value={ChatMessageCode.CHANNEL_UPDATE}>Update</option>
                <option value={ChatMessageCode.CHANNEL_REMOVE}>Remove</option>
              </select>
              <select
                className="persistent-select"
                value={searchFilters.persistent === undefined ? '' : searchFilters.persistent.toString()}
                onChange={(e) => handleFilterChange('persistent', e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">All Messages</option>
                <option value="true">Persistent</option>
                <option value="false">Non-Persistent</option>
              </select>
              <div className="search-buttons">
                <button
                  type="submit"
                  className="btn btn-primary search-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="pagination-section">
          <div className="pagination-buttons">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(0)}
              disabled={messages.length === 0 || loading}
              title="First page"
            >
              <PageFirstIcon />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(-1)}
              disabled={!prevCursor || loading}
              title="Previous page"
            >
              <PagePrevIcon />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(1)}
              disabled={!nextCursor || loading}
              title="Next page"
            >
              <PageNextIcon />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error when querying chat messages:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading messages...</strong>
        </div>
      )}

      <div className="chat-messages-table-container">
        <table className="chat-messages-table">
          <thead>
            <tr>
              <th style={{ width: '120px' }}>Type</th>
              <th style={{ width: '200px' }}>Sender</th>
              <th>Content</th>
              <th style={{ width: '100px' }}>Channel Type</th>
              <th style={{ width: '80px' }}>Persistent</th>
              <th style={{ width: '180px' }}>Created</th>
              {deleteAllowed() && <th style={{ width: '90px' }}>Remove</th>}
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 && !loading && (
              <tr>
                <td colSpan={deleteAllowed() ? 7 : 6} className="no-messages">
                  No messages found - change the filter criteria.
                </td>
              </tr>
            )}
            {messages.map((message, index) => (
              <tr key={message.message_id} className="message-row">
                <td onClick={() => handleViewMessage(message)} className="clickable">
                  <span className={`type-badge ${getMessageTypeBadgeClass(message.code)}`}>
                    {getMessageTypeText(message.code)}
                  </span>
                </td>
                <td onClick={() => handleViewMessage(message)} className="clickable">
                  <div className="sender-info">
                    <div className="username">{message.username || '-'}</div>
                    <code className="sender-id">{message.sender_id}</code>
                  </div>
                </td>
                <td onClick={() => handleViewMessage(message)} className="clickable">
                  <div className="message-content">
                    {truncateContent(message.content)}
                  </div>
                </td>
                <td onClick={() => handleViewMessage(message)} className="clickable">
                  <span className="channel-type">{getChannelType(message)}</span>
                </td>
                <td onClick={() => handleViewMessage(message)} className="clickable">
                  <span className={`persistent-badge ${message.persistent ? 'persistent' : 'temporary'}`}>
                    {message.persistent ? 'Yes' : 'No'}
                  </span>
                </td>
                <td onClick={() => handleViewMessage(message)} className="clickable">
                  {formatDate(message.create_time)}
                </td>
                {deleteAllowed() && (
                  <td className="delete-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteMessage(message, index)}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default ChatMessages;
